import { useContext, useMemo } from "react";
import { CreateAccount } from "./create-account/create-account";
import HomeAddress from "./home-address/home-address";
import ContactDetails from "./contact-details/contact-details";
import CheckoutContext from "../../checkout-context";
import PersonalDetails from "./personal-details/personal-details";
import { InvoiceOptionsSlider } from "./invoice-options/invoice-option-slider/InvoiceOptionsSlider";
import { ManageInvoiceOptionsDrawer } from "./invoice-options/manage-invoice-options/ManageInvoiceOptionsDrawer";
import { useState, useEffect } from "react";
import { isCompanyDetails } from "./utils/IsCompanyDetails";

export function CustomerData({ showInvoiceOptions = true }) {
  const {
    data: checkoutData,
    accountAgreement,
    accountPrivacyAgreement,
    changeAccountAgreement,
    changeAccountPrivacyAgreement,
    textsMapping,
    getPersonalAddressDetails,
    defaultCountryRadioButtonChanged,
    customerAddressNotFoundErrorMessage,
    trimInput,
    setAddress,
    setCompanies,
    setCompanyInvoiceDetails,
    setInvoiceEmail,
  } = useContext(CheckoutContext);

  const { genders, sectors, countries, customerData } = checkoutData;
  const { hasAddress, address, companies, isLoggedIn } = customerData;

  const options = useMemo(() => [address, ...companies], [address, companies]);

  //TS: "manage" | "add" | false
  const [openDrawer, setOpenDrawer] = useState(false);

  // Personal address is the default selection option.
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  // Detect changes in the companies array and update the company invoice details based on that if the updated item is the currently selected item.
  useEffect(() => {
    let selectedOption = options[selectedOptionIndex];

    if (isCompanyDetails(selectedOption)) {
      setCompanyInvoiceDetails(selectedOption);
    }
  }, [companies]);

  function handleSaveCompanyDetails(companyDetails, optionIndex) {
    // -1 Because the personal address is the first option in the options list and is not included in the companies list.
    let companiesIndex = optionIndex - 1;

    // Indicates that the option is newly created.
    if (typeof optionIndex !== "number") {
      companiesIndex = companies.length;
    }

    const newCompanies = [...companies];
    newCompanies[companiesIndex] = companyDetails;

    setCompanies(newCompanies);

    handleOptionSelect(optionIndex);
  }

  function handleSavePersonalAddress(address) {
    setAddress(address.address);

    handleOptionSelect(0);
  }

  function handleOptionSelect(key) {
    setSelectedOptionIndex(key);

    let selectedOption = options[key];

    if (isCompanyDetails(selectedOption)) {
      setCompanyInvoiceDetails(selectedOption);
      setInvoiceEmail(selectedOption.companyInvoiceEmail);
    } else {
      setCompanyInvoiceDetails(null);
      setInvoiceEmail("");
    }
  }

  function handleClose(selectedOptionIndex = null) {
    if (selectedOptionIndex !== null) {
      setSelectedOptionIndex(selectedOptionIndex);
    }

    setOpenDrawer(false);
  }

  if (hasAddress && showInvoiceOptions) {
    return (
      <section className="customer-data">
        <h2>{textsMapping.billingDataTitle}</h2>

        <InvoiceOptionsSlider
          textsMapping={textsMapping}
          options={options}
          onOptionSelect={handleOptionSelect}
          selectedOptionIndex={selectedOptionIndex}
          onOpenDrawer={setOpenDrawer}
          setInvoiceEmail={setInvoiceEmail}
        />

        {openDrawer && (
          <ManageInvoiceOptionsDrawer
            defaultShowInvoiceOptionSelector={openDrawer === "manage"}
            defaultSelectedOptionIndex={
              openDrawer !== "add" ? selectedOptionIndex : null
            }
            options={options}
            onClose={handleClose}
            onSavePersonalAddress={handleSavePersonalAddress}
            onSaveCompanyDetails={handleSaveCompanyDetails}
          />
        )}
      </section>
    );
  }

  return (
    <section className="customer-data">
      <h2>{textsMapping.customerDataTitle}</h2>

      {!isLoggedIn && (
        <CreateAccount
          textsMapping={textsMapping}
          accountAgreement={accountAgreement}
          accountPrivacyAgreement={accountPrivacyAgreement}
          changeAccountAgreement={changeAccountAgreement}
          changeAccountPrivacyAgreement={changeAccountPrivacyAgreement}
          isLoggedIn={isLoggedIn}
          trimInput={trimInput}
        />
      )}

      <PersonalDetails
        textsMapping={textsMapping}
        genders={genders}
        sectors={sectors}
      />

      <HomeAddress
        textsMapping={textsMapping}
        getPersonalAddressDetails={getPersonalAddressDetails}
        defaultCountryRadioButtonChanged={defaultCountryRadioButtonChanged}
        customerAddressNotFoundErrorMessage={
          customerAddressNotFoundErrorMessage
        }
        defaultCountry={address.defaultCountry}
        countries={countries}
      />

      <ContactDetails textsMapping={textsMapping} />
    </section>
  );
}
